import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import LanguageSwitcher from "../components/LanguageSwitcher";

const CANDIDATE_STATUSES_BLOCKED_FROM_LOGIN = ["completed", "paused", "failed"];

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    usernameOrPhone: "",
    password: "",
    rememberMe: true, // Default to true
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Fade-in animation on mount
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Show message when redirected after candidate account is no longer active
  useEffect(() => {
    if (location.state?.candidateAccountInactive) {
      setLoginError(t("login.errors.candidateAccountInactive"));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.candidateAccountInactive, location.pathname, navigate, t]);

  // Real-time validation
  const validateField = (name, value) => {
    let error = "";

    if (name === "usernameOrPhone") {
      if (!value.trim()) {
        error = t("login.errors.usernameRequired");
      } else if (value.trim().length < 3) {
        error = t("login.errors.usernameLength");
      }
    }

    if (name === "password") {
      if (!value) {
        error = t("login.errors.passwordRequired");
      } else if (value.length < 6) {
        error = t("login.errors.passwordLength");
      }
    }

    return error;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    // Clear login error when user starts typing
    if (loginError) {
      setLoginError("");
    }

    // Only validate if field has been touched (blurred before)
    if (type !== "checkbox" && touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  // Handle input blur - validate and show errors
  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate and show error
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      usernameOrPhone: true,
      password: true,
    });

    // Validate all fields
    const usernameError = validateField(
      "usernameOrPhone",
      formData.usernameOrPhone
    );
    const passwordError = validateField("password", formData.password);

    if (usernameError || passwordError) {
      setErrors({
        usernameOrPhone: usernameError,
        password: passwordError,
      });
      return;
    }

    setIsLoading(true);
    setLoginError("");

    try {
      // Set persistence based on "Remember me" checkbox
      if (formData.rememberMe) {
        await setPersistence(auth, browserLocalPersistence);
      } else {
        await setPersistence(auth, browserSessionPersistence);
      }

      // Use the username/phone as email (assuming format: username@autoshkollaburimi.com)
      // Or if it's an email, use it directly
      let email = formData.usernameOrPhone;

      // If it doesn't contain @, assume it's a username and convert to email
      if (!email.includes("@")) {
        email = `${email}@autoshkollaburimi.com`;
      }

      // Sign in with Firebase Authentication
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        formData.password
      );

      // Get user data from Firestore
      const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log("User logged in:", userData);

        const userRole = userData.role;

        // Candidates with completed, paused, or failed status cannot log in
        if (userRole === "candidate" && userData.entityId) {
          const candidateDoc = await getDoc(
            doc(db, "candidates", userData.entityId)
          );
          if (candidateDoc.exists()) {
            const candidateStatus = candidateDoc.data().status;
            if (
              candidateStatus &&
              CANDIDATE_STATUSES_BLOCKED_FROM_LOGIN.includes(candidateStatus)
            ) {
              await signOut(auth);
              setLoginError(t("login.errors.candidateAccountInactive"));
              setIsLoading(false);
              return;
            }
          }
        }

        // Store user info in localStorage if "Remember me" is checked
        if (formData.rememberMe) {
          localStorage.setItem("userEmail", email);
        }

        // Navigate based on user role
        if (userRole === "admin") {
          navigate("/dashboard/overview");
        } else if (userRole === "instructor") {
          navigate("/dashboard/reports", { state: { openAddReport: true } });
        } else if (userRole === "candidate") {
          navigate("/dashboard/lessons");
        } else {
          // Default fallback
          navigate("/dashboard/lessons");
        }
      } else {
        // User document doesn't exist - this shouldn't happen but handle gracefully
        console.error("User document not found in Firestore");
        setLoginError(
          "User account not properly configured. Please contact administrator."
        );
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.error("Login error:", error);

      // Handle specific Firebase auth errors
      let errorMessage = "Invalid username or password. Please try again.";

      if (error.code === "auth/user-not-found") {
        errorMessage = "No account found with this username.";
      } else if (error.code === "auth/wrong-password") {
        errorMessage = "Incorrect password. Please try again.";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many failed attempts. Please try again later.";
      } else if (error.code === "auth/network-request-failed") {
        errorMessage = "Network error. Please check your connection.";
      }

      setLoginError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="min-h-screen flex">
      {/* LEFT SIDE - White form section */}
      <div
        className={`w-full lg:w-1/2 bg-white flex items-center justify-center p-6 sm:p-12 transition-all duration-1000 ${
          isVisible ? "opacity-100 -translate-x-0" : "opacity-0 -translate-x-8"
        }`}
      >
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center space-x-3 mb-12">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
              <img
                src="/logoBlack.png"
                alt="Logo"
                className="w-6 h-6 object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                  const svg = document.createElementNS(
                    "http://www.w3.org/2000/svg",
                    "svg"
                  );
                  svg.setAttribute("class", "w-6 h-6 text-white");
                  svg.setAttribute("fill", "none");
                  svg.setAttribute("stroke", "currentColor");
                  svg.setAttribute("viewBox", "0 0 24 24");
                  svg.innerHTML =
                    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>';
                  e.target.parentElement.appendChild(svg);
                }}
              />
            </div>
            <span className="text-black text-xl font-bold">Driving School</span>
          </div>

          {/* Language Switcher - Mobile */}
          <div className="lg:hidden mb-6">
            <LanguageSwitcher />
          </div>

          {/* Header */}
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-black mb-2">
              {t("login.title")}
            </h2>
            <p className="text-gray-600">{t("login.subtitle")}</p>
          </div>

          {/* Error message */}
          {loginError && (
            <div className="mb-6 p-4 bg-black/5 border border-black/10 rounded-lg text-gray-900 text-sm animate-shake-smooth">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="font-semibold">Authentication Failed</p>
                  <p className="text-xs text-gray-600 mt-0.5">{loginError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {/* Username */}
            <div>
              <label
                htmlFor="usernameOrPhone"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                {t("login.username")}
              </label>
              <input
                type="text"
                id="usernameOrPhone"
                name="usernameOrPhone"
                value={formData.usernameOrPhone}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 bg-white border ${
                  touched.usernameOrPhone && errors.usernameOrPhone
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-black focus:ring-black/10"
                } rounded-lg text-gray-900 placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-4`}
                placeholder={t("login.placeholders.username")}
                aria-required="true"
                aria-invalid={errors.usernameOrPhone ? "true" : "false"}
              />
              {touched.usernameOrPhone && errors.usernameOrPhone && (
                <p className="mt-2 text-xs text-gray-700 animate-slide-down">
                  {errors.usernameOrPhone}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                {t("login.password")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-3 pr-12 bg-white border ${
                    touched.password && errors.password
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-300 focus:border-black focus:ring-black/10"
                  } rounded-lg text-gray-900 placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-4`}
                  placeholder={t("login.placeholders.password")}
                  aria-required="true"
                  aria-invalid={errors.password ? "true" : "false"}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors duration-200 p-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {touched.password && errors.password && (
                <p className="mt-2 text-xs text-gray-700 animate-slide-down">
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="w-4 h-4 border-2 border-gray-300 rounded text-black focus:ring-2 focus:ring-black/10 cursor-pointer transition-all"
                />
                <span className="ml-2 text-sm text-gray-700 group-hover:text-black transition-colors">
                  {t("login.rememberMe")}
                </span>
              </label>
              <a
                href="/forgot-password"
                className="text-sm font-medium text-gray-700 hover:text-black transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/forgot-password");
                }}
              >
                {t("login.forgotPassword")}
              </a>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-6 px-4 py-3 bg-black text-white font-medium rounded-lg transition-all duration-200 hover:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-black/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("login.loggingIn")}
                </span>
              ) : (
                t("login.loginButton")
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-600">
            {t("login.noAccount")}{" "}
            <a
              href="/contact"
              className="font-medium text-black hover:underline"
            >
              {t("login.contactAdmin")}
            </a>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - Black branding section with car */}
      <div
        className={`hidden lg:flex lg:w-1/2 bg-black relative overflow-hidden transition-all duration-1000 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        {/* Diagonal light beam effect */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 blur-3xl rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white opacity-5 blur-3xl rounded-full transform -translate-x-32 translate-y-32"></div>

        {/* Subtle road lines pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="h-full w-full"
            style={{
              backgroundImage: `
                repeating-linear-gradient(
                  90deg,
                  transparent,
                  transparent 50px,
                  rgba(255, 255, 255, 0.5) 50px,
                  rgba(255, 255, 255, 0.5) 54px
                )
              `,
              backgroundPosition: "0 50%",
            }}
          ></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Top section */}
          <div className="flex justify-between items-center">
            <LanguageSwitcher />
            <div className="flex items-center space-x-2 text-white/60 text-sm">
              <div className="w-2 h-2 bg-white/60 rounded-full animate-pulse"></div>
              <span>Auto Shkolla </span>
            </div>
          </div>

          {/* Center content */}
          <div className="flex-1 flex items-center justify-center">
            <div className="max-w-lg text-center">
              {/* Car icon */}
              <div className="mb-12 flex justify-center">
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-white blur-2xl opacity-10 rounded-full scale-150"></div>

                  {/* Car illustration */}
                  <svg
                    className="w-48 h-48 text-white relative z-10"
                    viewBox="0 0 200 200"
                    fill="none"
                    stroke="currentColor"
                  >
                    {/* Car body */}
                    <path
                      d="M40 120 L50 100 L70 85 L130 85 L150 100 L160 120 Z"
                      fill="currentColor"
                      opacity="0.9"
                      strokeWidth="2"
                    />
                    {/* Car roof */}
                    <path
                      d="M65 100 L75 75 L125 75 L135 100"
                      fill="currentColor"
                      opacity="0.7"
                      strokeWidth="2"
                    />
                    {/* Windows */}
                    <path
                      d="M70 98 L77 80 L95 80 L98 98"
                      fill="none"
                      strokeWidth="2"
                      opacity="0.5"
                    />
                    <path
                      d="M102 98 L105 80 L123 80 L130 98"
                      fill="none"
                      strokeWidth="2"
                      opacity="0.5"
                    />
                    {/* Wheels */}
                    <circle
                      cx="70"
                      cy="120"
                      r="12"
                      fill="none"
                      strokeWidth="3"
                      opacity="0.9"
                    />
                    <circle
                      cx="70"
                      cy="120"
                      r="6"
                      fill="currentColor"
                      opacity="0.7"
                    />
                    <circle
                      cx="130"
                      cy="120"
                      r="12"
                      fill="none"
                      strokeWidth="3"
                      opacity="0.9"
                    />
                    <circle
                      cx="130"
                      cy="120"
                      r="6"
                      fill="currentColor"
                      opacity="0.7"
                    />
                    {/* Headlight */}
                    <circle
                      cx="155"
                      cy="110"
                      r="3"
                      fill="currentColor"
                      opacity="0.8"
                    />
                    {/* Bottom line */}
                    <line
                      x1="50"
                      y1="120"
                      x2="40"
                      y2="120"
                      strokeWidth="2"
                      opacity="0.6"
                    />
                    <line
                      x1="150"
                      y1="120"
                      x2="160"
                      y2="120"
                      strokeWidth="2"
                      opacity="0.6"
                    />

                    {/* Motion lines */}
                    <line
                      x1="20"
                      y1="95"
                      x2="35"
                      y2="95"
                      strokeWidth="2"
                      opacity="0.3"
                      className="animate-dash"
                    />
                    <line
                      x1="15"
                      y1="110"
                      x2="35"
                      y2="110"
                      strokeWidth="2"
                      opacity="0.3"
                      className="animate-dash"
                    />
                    <line
                      x1="25"
                      y1="125"
                      x2="40"
                      y2="125"
                      strokeWidth="2"
                      opacity="0.3"
                      className="animate-dash"
                    />
                  </svg>
                </div>
              </div>

              {/* Headline */}
              <h1 className="text-6xl font-bold text-white mb-6 leading-tight">
                {t("login.branding.headline")}
              </h1>

              {/* Tagline */}
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                {t("login.branding.tagline")}
              </p>

              {/* Stats */}
              <div className="flex justify-center items-center space-x-12 text-center">
                <div>
                  <div className="text-3xl font-bold text-white mb-1">3K+</div>
                  <div className="text-gray-500 text-sm">
                    {t("login.branding.stat1")}
                  </div>
                </div>
                <div className="w-px h-12 bg-white/10"></div>
                <div>
                  <div className="text-3xl font-bold text-white mb-1">20+</div>
                  <div className="text-gray-500 text-sm">
                    {t("login.branding.stat2")}
                  </div>
                </div>
                <div className="w-px h-12 bg-white/10"></div>
                <div>
                  <div className="text-3xl font-bold text-white mb-1">95%</div>
                  <div className="text-gray-500 text-sm">
                    {t("login.branding.stat3")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="text-gray-500 text-sm">{t("login.copyright")}</div>
            <div className="text-gray-500 text-sm">
              {t("login.developedBy")}{" "}
              <a
                href="https://www.ardbraha.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:underline transition-all duration-200 font-medium"
              >
                Ard Braha
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes shake-smooth {
          0%,
          100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-4px);
          }
          75% {
            transform: translateX(4px);
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes dash {
          0% {
            opacity: 0.1;
            transform: translateX(0);
          }
          50% {
            opacity: 0.3;
          }
          100% {
            opacity: 0.1;
            transform: translateX(-20px);
          }
        }

        .animate-shake-smooth {
          animation: shake-smooth 0.5s ease-out;
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }

        .animate-dash {
          animation: dash 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default Login;
